import EngineCleanupBase from '../EngineCleanupBase';
import ComponentNotifier from '../../wss/ComponentNotifier';
import { subscribe } from '../../utils/pubsub';
import { events } from '../../wss/SystemEvents';
import { EngineNames } from '../../wss/events/EngineNames';
import logCache from './LogCache';
import logCacheStorage from './LogCacheStorage';
import logPublisher from './LogPublisher';

const _name = EngineNames.LogEngine;

/**
 * Orchestrates FE error logging.
 * Subscribes to onLogCreated — buffers entries via LogCache + LogCacheStorage.
 * Subscribes to onReconnected — flushes buffered entries to BE via LogPublisher.
 * In-memory mirror is loaded from localStorage on bootup so entries survive F5.
 */
class LogEngine extends EngineCleanupBase {
    _onLogCreated = null;
    _onReconnected = null;

    bootup = () => {
        this.initialize(_name);

        // Load persisted entries from localStorage into in-memory cache
        const persisted = logCacheStorage.load();
        if (persisted.length > 0) {
            logCache.setCache(persisted);
        }

        // Subscribe to new log entries
        this._onLogCreated = subscribe(events.onLogCreated, (entry) => this._handleEntry(entry));

        // Subscribe to reconnect — flush buffered entries to BE
        this._onReconnected = subscribe(events.onReconnected, () => this._handleReconnect());
    };

    /**
     * Returns all buffered entries for display (e.g. LogTab).
     * @returns {{ app, uid, source, method, message, ems, count, lastSeen }[]}
     */
    getEntries = () => logCache.getAll();

    /**
     * Clears in-memory cache and localStorage.
     * Calls super.clear() to release the onClearCache subscription.
     */
    clear = () => {
        super.clear();
        logCache.clear();
        logCacheStorage.clear();
        this._notify();
    };

    // ─── private ──────────────────────────────────────────

    /**
     * Handles a new log entry — adds to cache, persists, notifies UI.
     * @param {{ app, uid, source, method, message, ems }} entry
     */
    _handleEntry = (entry) => {
        logCache.add(entry);
        logCacheStorage.save(logCache.getAll());
        this._notify();
    };

    /**
     * Flushes buffered entries to BE on reconnect.
     * Entries are cleared from storage only after confirmed POST — see LogPublisher.
     */
    _handleReconnect = async () => {
        const entries = logCache.flush();
        if (entries.length === 0) return;

        // Notify UI immediately — cache is now empty
        this._notify();

        await logPublisher.publish(entries);
    };

    /**
     * Notifies UI components that the log entry list has changed.
     */
    _notify = () => {
        ComponentNotifier.notify(_name, 'logsChanged', { entries: logCache.getAll() });
    };
}

const logEngine = new LogEngine();
export default logEngine;
